import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { money, pct } from "../lib/format";
import { trailingQuarters, downloadCsv, monthLabel } from "../lib/quarters";
import { useAuth } from "../lib/auth";
import { BookViewBar, DEFAULT_VIEW } from "./BookViewBar";

const COLUMNS = [
  { key: "company", label: "Employer", numeric: false },
  { key: "carrier", label: "Carrier", numeric: false },
  { key: "premium", label: "Premium", money: true },
  { key: "claims_reserves", label: "Claims + Reserves", money: true },
  { key: "loss_ratio", label: "Loss ratio", percent: true },
  { key: "expenses", label: "Expenses", money: true },
  { key: "surplus", label: "Surplus", money: true },
  { key: "errs", label: "ERRs", money: true },
];

export default function BookSummary() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [sort, setSort] = useState({ key: "premium", dir: "desc" });
  const { user } = useAuth();
  const viewKey = `book-view:${user?.id || "anon"}`;
  const [viewId, setViewIdState] = useState(() => localStorage.getItem(viewKey) || DEFAULT_VIEW);
  const [views, setViews] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const setViewId = (id) => { setViewIdState(id); localStorage.setItem(viewKey, id); };
  const loadViews = useCallback(() => api.get("/admin/book-summary/views").then(({ data: d }) => {
    setViews(d.views);
    if (viewId && !d.views.some((v) => v.id === viewId)) setViewId(DEFAULT_VIEW);
  }).catch(() => {}), [viewId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadViews(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fallback = useMemo(() => trailingQuarters(8), []);
  const from = params.get("from") || fallback.from;
  const to = params.get("to") || fallback.to;
  const product = params.get("product") || "ALL";
  const planType = params.get("plan") || "";
  const insurer = params.get("insurer") || "";
  const carrier = params.get("carrier") || "";

  useEffect(() => {
    let cancelled = false;
    api.get("/financials/book-summary", {
      params: { from_quarter: from, to_quarter: to, product, plan_type: planType, insurer, carrier,
                view: viewId || undefined, show_all: showAll || undefined },
    })
      .then(({ data: payload }) => { if (!cancelled) setData(payload); })
      .catch((error) => {
        if (error.response?.status === 404 && viewId) { setViewId(DEFAULT_VIEW); return; }
        toast.error(formatApiError(error.response?.data?.detail));
      });
    return () => { cancelled = true; };
  }, [from, to, product, planType, insurer, carrier, viewId, showAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const list = [...(data?.rows || [])];
    const factor = sort.dir === "asc" ? 1 : -1;
    list.sort((a, b) => (typeof a[sort.key] === "string"
      ? a[sort.key].localeCompare(b[sort.key]) * factor
      : (a[sort.key] - b[sort.key]) * factor));
    return list;
  }, [data, sort]);

  const setSortKey = (key) =>
    setSort((current) => ({ key, dir: current.key === key && current.dir === "desc" ? "asc" : "desc" }));

  const cell = (row, column) => {
    if (column.money) return money(row[column.key]);
    if (column.percent) return pct(row[column.key]);
    return row[column.key];
  };

  if (!data) return null;

  return (
    <div className="mt-10" data-testid="book-summary">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 border-b pb-4 mb-4" style={{ borderColor: "var(--border)" }}>
        <div>
          <div className="eyebrow">Book summary</div>
          <h2 className="font-heading font-black text-3xl md:text-4xl mt-1 leading-none tracking-tight" style={{ color: "var(--ink)" }}>
            Every employer, ranked.
          </h2>
          <p className="mt-2 text-sm" style={{ color: "var(--muted)" }} data-testid="book-summary-meta">
            {data.client_count} employer(s) with data in {data.quarters[0]}–{data.quarters[data.quarters.length - 1]} ·{" "}
            {data.over_target_count} above the {Number(data.target_loss_ratio).toFixed(0)}% target · click a column to re-rank · follows the program filters below
            {data.hidden_count > 0 && (
              <span data-testid="book-summary-hidden-note"> · {data.hidden_count} hidden{viewId ? " by this view" : <> (<Link to="/admin/setup#book-summary-visibility" className="underline">change in Settings</Link>)</>}</span>
            )}
          </p>
        </div>
        <button
          className="btn-ghost text-xs self-start sm:self-auto"
          onClick={() => downloadCsv("book-summary.csv", rows, COLUMNS.map((column) => ({ key: column.key, label: column.label })))}
          data-testid="book-summary-csv"
        >
          Download CSV
        </button>
      </div>

      <BookViewBar views={views} viewId={viewId} onViewChange={setViewId} showAll={showAll} onShowAll={setShowAll}
                   visibleNames={(data.rows || []).map((r) => r.company)} onViewsChanged={loadViews} />

      <div className="border overflow-x-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <table className="w-full text-sm" data-testid="book-summary-table">
          <thead style={{ background: "#F3F3F1" }}>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column.key}
                  onClick={() => setSortKey(column.key)}
                  className={`px-4 py-3 eyebrow cursor-pointer whitespace-nowrap ${column.key === "company" ? "text-left" : "text-right"}`}
                  data-testid={`book-sort-${column.key}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {column.label}
                    {sort.key === column.key && (sort.dir === "desc" ? <ArrowDown size={11} /> : <ArrowUp size={11} />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.company} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`book-row-${row.company}`}>
                {COLUMNS.map((column) => (
                  <td
                    key={column.key}
                    className={`px-4 py-3 whitespace-nowrap ${["company", "carrier"].includes(column.key) ? "text-left" : "text-right"}`}
                    style={{ color: "var(--ink)" }}
                  >
                    {column.key === "company" ? (
                      <span className="flex items-center gap-2">
                        <span className="eyebrow text-[9px]" style={{ color: "var(--muted)" }}>#{index + 1}</span>
                        <span className="font-medium">{row.company}</span>

                        {row.data_through && (
                          <span className="text-[10px]" style={{ color: "var(--muted)" }}>
                            through {monthLabel(row.data_through)}
                          </span>
                        )}
                      </span>
                    ) : column.key === "carrier" ? (
                      <span style={{ color: "var(--muted)" }}>{row.carrier || "—"}</span>
                    ) : column.key === "loss_ratio" ? (
                      <span className="inline-flex items-center gap-1.5 font-heading font-bold"
                            style={{ color: row.over_target ? "#B4472D" : "var(--ink)" }}>
                        {row.over_target && <AlertTriangle size={12} />}
                        {pct(row.loss_ratio)}
                      </span>
                    ) : column.key === "errs" ? (
                      <span title={row.surplus_word}>{money(row.errs)}</span>
                    ) : cell(row, column)}
                  </td>
                ))}
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={COLUMNS.length} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="book-summary-empty">
                No employer has feed data in this range.
              </td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot style={{ background: "#F3F3F1" }}>
              <tr className="border-t" style={{ borderColor: "var(--border)" }} data-testid="book-summary-totals">
                <td className="px-4 py-3 eyebrow text-left">Book total</td>
                <td className="px-4 py-3" />
                <td className="px-4 py-3 text-right font-heading font-bold">{money(data.totals.premium)}</td>
                <td className="px-4 py-3 text-right font-heading font-bold">{money(data.totals.claims_reserves)}</td>
                <td className="px-4 py-3 text-right font-heading font-bold">{pct(data.totals.loss_ratio)}</td>
                <td className="px-4 py-3 text-right font-heading font-bold">{money(data.totals.expenses)}</td>
                <td className="px-4 py-3 text-right font-heading font-bold">{money(data.totals.surplus)}</td>
                <td className="px-4 py-3 text-right font-heading font-bold">{money(data.totals.errs)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
